import { Button } from "components/DesignSystem/Button/Button";
import { LockSecure } from "components/icons/LockSecure";
import { EditPencil } from "components/icons/EditPencil";
import { PlusIcon } from "components/icons/PlusIcon";
import ToolTip from "components/design/toolTip";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useState } from "react";
import {
  useGetAllSensitiveDataTypesQuery,
  useGetAllSensitiveDataQuery,
} from "apis/sensitiveData";
import { SensitiveDataFormField } from "types/Models/sensitiveData";
import {
  SensitiveDataDynamicForm,
  EditSensitiveDataDynamicForm,
} from "components/SensitiveData/SensitiveData";
import { SensitiveDataCopyButton } from "components/SensitiveData/SensitiveDataCopyButton";
import {
  SENSITIVE_DATA_TYPE_CREDENTIALS,
  SENSITIVE_DATA_MASKED_VALUE,
} from "constants/sensitiveData";
import { CollapsibleCard } from "./CollapsibleCard";

const getFieldDisplayValue = (
  field: SensitiveDataFormField
): string | undefined => {
  if (field.is_encrypted) {
    return field.value_display ? SENSITIVE_DATA_MASKED_VALUE : undefined;
  }
  return (field.value as string) ?? undefined;
};

type CredentialRowProps = {
  entityId: string;
  formId: string;
  field: SensitiveDataFormField;
};

const CredentialRow = ({ entityId, formId, field }: CredentialRowProps) => {
  const displayValue = getFieldDisplayValue(field);
  const valueSpan = (
    <span className="t-min-w-0 t-truncate t-text-body-sm t-text-text-100 t-font-mono">
      {displayValue}
    </span>
  );

  return (
    <div className="t-flex t-items-center t-justify-between t-gap-4 t-px-5 t-py-3">
      <span className="t-shrink-0 t-text-body-sm t-text-text-60">
        {field.key}
      </span>
      <div className="t-flex t-min-w-0 t-items-center t-gap-2">
        {field.is_encrypted ? (
          valueSpan
        ) : (
          <ToolTip text={displayValue} propClass="t-max-w-xs">
            {valueSpan}
          </ToolTip>
        )}
        {field.is_copyable && (
          <SensitiveDataCopyButton
            entityId={entityId}
            formId={formId}
            attributeId={field.uuid}
            value={field.is_encrypted ? undefined : displayValue}
          />
        )}
      </div>
    </div>
  );
};

type CredentialInstanceProps = {
  entityId: string;
  instance: { uuid: string; form_data: SensitiveDataFormField[] };
  onEdit: (instanceId: string) => void;
};

const CredentialInstance = ({
  entityId,
  instance,
  onEdit,
}: CredentialInstanceProps) => {
  const fields = instance.form_data.filter((f) => getFieldDisplayValue(f));

  const handleEdit = () => onEdit(instance.uuid);

  return (
    <div className="t-border-0 t-border-b t-border-solid t-border-neutral-10 last:t-border-b-0">
      <div className="t-flex t-items-center t-justify-end t-px-5 t-pt-2">
        <Button
          customType="ghost_icon"
          size="xsmall"
          type="button"
          onClick={handleEdit}
        >
          <EditPencil />
        </Button>
      </div>
      <div>
        {fields.map((f) => (
          <CredentialRow
            key={f.field_key}
            entityId={entityId}
            formId={instance.uuid}
            field={f}
          />
        ))}
      </div>
    </div>
  );
};

type StateCredentialsCardProps = {
  stateId: string;
};

export const StateCredentialsCard = ({
  stateId,
}: StateCredentialsCardProps) => {
  const query = useQuery();
  const entityId = query.get("entity") ?? "";
  const addCredentialsModal = useModal();
  const editCredentialsModal = useModal();
  const [editingInstanceId, setEditingInstanceId] = useState<string | null>(
    null
  );

  const { data: allTypes = [] } = useGetAllSensitiveDataTypesQuery();
  const credentialsType = allTypes.find(
    (t) => t.name === SENSITIVE_DATA_TYPE_CREDENTIALS
  );
  const credentialsFormId = credentialsType?.uuid ?? null;

  const { data: allInstances = [] } = useGetAllSensitiveDataQuery(
    { entityId, countryStateId: stateId },
    { skip: !entityId || !stateId }
  );
  const stateInstances = allInstances.filter(
    (i) =>
      i.sensitive_data_type === SENSITIVE_DATA_TYPE_CREDENTIALS &&
      i.country_state_id === stateId
  );
  const instancesWithCredentials = stateInstances.filter((i) =>
    i.form_data.some((f) => getFieldDisplayValue(f))
  );
  const hasCredentials = instancesWithCredentials.length > 0;

  const handleEditInstance = (instanceId: string) => {
    setEditingInstanceId(instanceId);
    editCredentialsModal.open();
  };

  const headerTag = hasCredentials && (
    <Button
      customType="ghost_icon"
      size="xsmall"
      type="button"
      disabled={!credentialsFormId}
      onClick={addCredentialsModal.open}
    >
      <PlusIcon color="currentColor" />
    </Button>
  );

  return (
    <>
      <CollapsibleCard
        title="State credentials"
        icon={<LockSecure />}
        tag={headerTag}
        defaultOpen={false}
      >
        {hasCredentials ? (
          <div>
            {instancesWithCredentials.map((instance) => (
              <CredentialInstance
                key={instance.uuid}
                entityId={entityId}
                instance={instance}
                onEdit={handleEditInstance}
              />
            ))}
            <div className="t-p-3">
              <Button
                size="xsmall"
                customType="secondary"
                type="button"
                disabled={!credentialsFormId}
                onClick={addCredentialsModal.open}
              >
                Add another
              </Button>
            </div>
          </div>
        ) : (
          <div className="t-flex t-flex-col t-items-center t-justify-center t-py-6 t-gap-2 t-px-5">
            <span className="t-text-body-sm t-text-text-30">
              No credentials added
            </span>
            <Button
              size="xsmall"
              customType="secondary"
              type="button"
              disabled={!credentialsFormId}
              onClick={addCredentialsModal.open}
            >
              Add credentials
            </Button>
          </div>
        )}
      </CollapsibleCard>

      {editingInstanceId && (
        <EditSensitiveDataDynamicForm
          formId={editingInstanceId}
          isOpen={editCredentialsModal.isOpen}
          close={editCredentialsModal.close}
          entityId={entityId}
        />
      )}

      {credentialsFormId && (
        <SensitiveDataDynamicForm
          formId={credentialsFormId}
          formName={SENSITIVE_DATA_TYPE_CREDENTIALS}
          isOpen={addCredentialsModal.isOpen}
          close={addCredentialsModal.close}
          entityId={entityId}
          countryStateId={stateId}
        />
      )}
    </>
  );
};
